import { useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { ORDER } from "@/constants/testIds";
import { Upload, FileText, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FileThumb({ file, size = 40 }) {
  const isImage = ["jpg", "jpeg", "png"].includes(file.extension);
  if (file.has_thumbnail) {
    return (
      <img
        src={`${API}/files/${file.id}/thumb`}
        alt={file.original_name}
        loading="lazy"
        style={{ width: size, height: size * 1.3 }}
        className="object-cover object-top border border-[#E5E5E5] bg-white flex-shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size * 1.3 }}
      className="bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center flex-shrink-0"
    >
      {isImage ? <ImageIcon className="w-5 h-5 text-[#525252]" /> : <FileText className="w-5 h-5 text-[#525252]" />}
    </div>
  );
}

export default function UploadDropzone({ onUploaded }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onUploaded?.(data);
        toast.success(`Uploaded ${data.original_name} · ${data.pages} page${data.pages === 1 ? "" : "s"}`);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    upload(Array.from(e.dataTransfer.files));
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        data-testid={ORDER.dropzone}
        className={`cursor-pointer border-2 border-dashed border-[#E5E5E5] bg-[#F5F5F5] p-10 sm:p-14 text-center transition-all hover:border-[#059669] ${dragActive ? "dropzone-active" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          className="hidden"
          data-testid={ORDER.fileInput}
          onChange={(e) => upload(Array.from(e.target.files))}
        />
        <div className="flex flex-col items-center gap-3">
          {uploading ? (
            <Loader2 className="w-10 h-10 text-[#059669] animate-spin" />
          ) : (
            <div className="w-14 h-14 bg-[#0A0A0A] text-white flex items-center justify-center">
              <Upload className="w-7 h-7" strokeWidth={2.5} />
            </div>
          )}
          <div className="font-display font-bold text-2xl">
            {uploading ? "Uploading..." : "Drop your files here"}
          </div>
          <div className="text-sm text-[#525252]">
            or <span className="text-[#059669] font-bold underline underline-offset-4">click to browse</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">
            <span className="px-2 py-1 border border-[#E5E5E5]">PDF</span>
            <span className="px-2 py-1 border border-[#E5E5E5]">DOCX</span>
            <span className="px-2 py-1 border border-[#E5E5E5]">JPG</span>
            <span className="px-2 py-1 border border-[#E5E5E5]">PNG</span>
            <span className="px-2 py-1 border border-[#E5E5E5]">Max 250MB</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function UploadedFileRow({ file, onRemove, onPagesChange, children }) {
  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid={ORDER.fileItem}>
      <div className="flex items-center justify-between gap-3 p-4 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-3 min-w-0">
          <FileThumb file={file} size={48} />
          <div className="min-w-0">
            <div className="font-bold truncate text-sm">{file.original_name}</div>
            <div className="text-xs text-[#525252] mono flex items-center gap-2 flex-wrap">
              <span>{file.pages} page{file.pages === 1 ? "" : "s"}</span>
              {file.pages_estimated && (
                <span className="px-1.5 py-0.5 border border-[#F59E0B] text-[#F59E0B] text-[9px] uppercase tracking-[0.15em] font-bold">
                  Estimated
                </span>
              )}
              <span>· {(file.size_bytes / 1024).toFixed(0)} KB</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => onRemove(file.id)}
          data-testid={ORDER.removeFile}
          className="w-9 h-9 flex items-center justify-center border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] transition-colors flex-shrink-0"
          aria-label="Remove file"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {file.pages_estimated && onPagesChange && (
        <div className="px-4 pt-3 -mb-1">
          <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/40 px-3 py-2 text-xs text-[#92400E] flex items-center justify-between gap-3 flex-wrap">
            <span>
              Word doc page count is approximate. Please confirm or override below.
            </span>
            <label className="flex items-center gap-2 font-bold">
              Pages:
              <input
                type="number"
                min={1}
                value={file.pages}
                onChange={(e) => onPagesChange(file.id, Math.max(1, parseInt(e.target.value || "1")))}
                className="w-16 px-2 py-1 border border-[#0A0A0A] mono text-center bg-white outline-none"
                data-testid={`override-pages-${file.id}`}
              />
            </label>
          </div>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}
